import type {
  ProfileFilter,
  PlatformFilters,
} from '@/types/models/creator-crm/creatorCrm';
import type {PlatformId} from '@/types/models/creator-crm/filterId';
import type {SegmentFilters} from '@/types/models/segments/segment';
import type {CampaignListItem} from '@/hooks/queries/use-brand-campaigns-query/useBrandCampaignsQuery';
import {campaignPreferredLocationsToLocationValues} from './campaignPreferredLocations';

/* eslint-disable import/prefer-default-export -- named export for consistency with callers */

function getPlatformId(network: string): PlatformId {
  if (network.includes('instagram')) return 'instagram';
  if (network.includes('tiktok')) return 'tiktok';
  return network as PlatformId;
}

/**
 * Converts campaign data directly to SegmentFilters (MultiPlatformFilter).
 * Only async work is resolving locations via Mapbox (campaignPreferredLocationsToLocationValues).
 */
export async function campaignToSegmentFilters(
  campaign: CampaignListItem
): Promise<SegmentFilters> {
  const profile: ProfileFilter = {};
  const platforms: PlatformFilters = {};

  const {
    preferredCreatorAgeDetail,
    preferredCreatorGenders,
    preferredCreatorCities,
    preferredCreatorStates,
    preferredCreatorCountries,
    preferredCreatorSpecialties,
    network,
    unlocksAt,
    locksAbove,
  } = campaign;

  if (
    preferredCreatorAgeDetail?.minAge != null ||
    preferredCreatorAgeDetail?.maxAge != null
  ) {
    profile.age = {
      fromAge: preferredCreatorAgeDetail.minAge ?? 0,
      toAge: preferredCreatorAgeDetail.maxAge,
    };
  }

  const genderValue = preferredCreatorGenders?.[0]?.name?.toLowerCase();
  if (
    genderValue &&
    (genderValue === 'male' ||
      genderValue === 'female' ||
      genderValue === 'other')
  ) {
    profile.gender = genderValue;
  }

  if (preferredCreatorSpecialties?.length) {
    profile.categories = preferredCreatorSpecialties
      .filter((s): s is typeof s & {id: string; name: string} =>
        Boolean(s.id && s.name)
      )
      .map((s) => s.name);
  }

  const locations = await campaignPreferredLocationsToLocationValues({
    cities: preferredCreatorCities,
    states: preferredCreatorStates,
    countries: preferredCreatorCountries,
  });
  if (locations.length > 0) {
    profile.locations = locations;
  }

  const platformId = network ? getPlatformId(network) : null;
  if (platformId && (unlocksAt != null || locksAbove != null)) {
    platforms[platformId] = {
      followers: {min: unlocksAt, max: locksAbove ?? undefined},
    };
  } else if (platformId) {
    platforms[platformId] = {};
  }

  return {
    type: 'MultiPlatformFilter',
    ...(Object.keys(profile).length > 0 && {profile}),
    ...(Object.keys(platforms).length > 0 && {platforms}),
  };
}
